import { useMemo } from "react";
import type { Node, Edge } from "@xyflow/react";

export interface ValidationIssue {
  level: "error" | "warning";
  message: string;
  nodeId?: string;
  edgeId?: string;
}

/**
 * Validates a graph definition for structural correctness.
 * Returns a list of issues (errors prevent running, warnings are informational).
 */
export function useGraphValidation(nodes: Node[], edges: Edge[]): {
  issues: ValidationIssue[];
  hasErrors: boolean;
  hasWarnings: boolean;
} {
  return useMemo(() => {
    const issues: ValidationIssue[] = [];

    // Must have at least one start node
    const startNodes = nodes.filter((n) => n.type === "start" || n.type === "trigger");
    if (startNodes.length === 0) {
      issues.push({ level: "error", message: "Agent must have a Start or Trigger node" });
    }

    // Must have at least one end node
    const endNodes = nodes.filter((n) => n.type === "end");
    if (endNodes.length === 0) {
      issues.push({ level: "error", message: "Agent must have an End node" });
    }

    // Check for orphan nodes (no incoming or outgoing edges, excluding start/end)
    for (const node of nodes) {
      if (node.type === "start" || node.type === "trigger" || node.type === "end") continue;
      const hasIncoming = edges.some((e) => e.target === node.id);
      const hasOutgoing = edges.some((e) => e.source === node.id);
      if (!hasIncoming && !hasOutgoing) {
        issues.push({
          level: "warning",
          message: `Node "${(node.data as Record<string, unknown>)?.name ?? node.id}" is disconnected`,
          nodeId: node.id,
        });
      }
    }

    // Check router nodes have labeled edges
    const routerNodes = nodes.filter((n) => n.type === "router");
    for (const router of routerNodes) {
      const outEdges = edges.filter((e) => e.source === router.id);
      const unlabeled = outEdges.filter((e) => !e.label);
      if (unlabeled.length > 0 && outEdges.length > 1) {
        issues.push({
          level: "warning",
          message: `Router "${(router.data as Record<string, unknown>)?.name ?? router.id}" has unlabeled edges`,
          nodeId: router.id,
        });
      }
    }

    // Check reachability from start
    if (startNodes.length > 0) {
      const reachable = new Set<string>();
      const queue = startNodes.map((n) => n.id);
      while (queue.length > 0) {
        const current = queue.shift()!;
        if (reachable.has(current)) continue;
        reachable.add(current);
        for (const edge of edges) {
          if (edge.source === current && !reachable.has(edge.target)) {
            queue.push(edge.target);
          }
        }
      }
      const unreachable = nodes.filter((n) => !reachable.has(n.id) && n.type !== "start" && n.type !== "trigger");
      if (unreachable.length > 0) {
        issues.push({
          level: "warning",
          message: `${unreachable.length} node(s) not reachable from Start`,
        });
      }
    }

    // Check end nodes are reachable
    for (const end of endNodes) {
      const hasIncoming = edges.some((e) => e.target === end.id);
      if (!hasIncoming) {
        issues.push({
          level: "warning",
          message: "End node has no incoming edges",
          nodeId: end.id,
        });
      }
    }

    return {
      issues,
      hasErrors: issues.some((i) => i.level === "error"),
      hasWarnings: issues.some((i) => i.level === "warning"),
    };
  }, [nodes, edges]);
}
